import React, { useState, useEffect, useContext, useCallback } from "react";
import { useApi } from "../../api/useApi";
import {
  usersInEnabledWorkGroups,
  availableDataSourcesForUserId,
} from "../../api/userQueries";
import { useApi as useApiRedux } from "../../api/useApiRedux";
import PagedTable from "../../components/Table/PagedTable";
import {
  StyledSelect,
  Label,
  FormControl,
} from "../../components/Form/FormControls";
import { setUsersRole } from "../../api/userMutations";
import update from "immutability-helper";
import { availableOrganizations } from "../../api/organizationQueries";
import ErrorMessages from "../../components/Notifications/ErrorMessages";
import { formatTime } from "../../common/formats";
import Modal from "../../components/Modal";
import TableButton from "../../components/Button/TableButton";
import { AuthContext } from "../../contexts/AuthContext";
import SpinningLoader from "../../components/Loaders/SpinningLoader";
import SortTable from "../../components/Table/SortTable";
import Button from "../../components/Button/index";
import Spinner from "../../components/Loaders/Spinner";
import { trackCustomEvent } from "../../appInsights";
import { useSearch } from "../../Hooks/useSearch";
import SearchInput from "../../components/Forms/Search/SearchInput";

// availableDataSourcesForUserId
const ViewUsersDataSourcePermission = ({ viewUser }) => {
  const { user } = useContext(AuthContext);

  const [{ loading, data: apiData }, doFetch] = useApi();

  useEffect(() => {
    if (viewUser && user) {
      doFetch({
        query: availableDataSourcesForUserId,
        variables: {
          userId: Number(viewUser?.id),
          where: {
            enabled: { eq: true },
          },
        },
      });
    }
  }, [viewUser, user, doFetch]);

  const data =
    apiData?.availableDataSourcesForUserId?.edges?.filter(
      (source) => source?.node?.id !== -2
    ) ?? [];

  const permissionRoles = [
    { label: "Full", value: 15 },
    { label: "Elevated", value: 7 },
    { label: "Read Only", value: 1 },
    { label: "None", value: 0 },
  ];
  const columnsData = [
    {
      Header: "Name",
      id: "name",
      accessor: (d) => d?.node?.name,
    },
    {
      Header: "Permission",
      id: "permission",
      Cell: ({ row: { original } }) => {
        return (
          <div>
            {original?.node?.permissionsForUser?.map((perm) => {
              return (
                <div>
                  <div>
                    Group: {perm?.accessingWorkGroup?.displayName ?? "Admin"}
                  </div>
                  <div>
                    Permission:{" "}
                    {
                      permissionRoles.find(
                        (pr) => pr?.value === perm?.permissionCode
                      )?.label
                    }
                  </div>
                </div>
              );
            })}
            {!original?.node?.permissionsForUser?.length ? (
              <div>
                <div>Admin Role</div>
                <div>Permission: Full</div>
              </div>
            ) : null}
          </div>
        );
      },
    },
  ];

  if (loading)
    return <SpinningLoader text="Loading Users Source Permissions" />;
  return (
    <div>
      <div>
        <div>{viewUser?.displayName}</div>
        <div>{viewUser?.emailAddress}</div>
      </div>

      <SortTable
        dontReset={true}
        data={data}
        columns={columnsData}
        loading={loading}
        hidePagination={true}
        defaultPageSize={9999}
      />
    </div>
  );
};

const StaticUsersSection = () => {
  const [selectedOrg, setSelectedOrg] = useState({
    label: "Entire Platform",
    value: "Entire Platform",
  });

  return (
    <>
      <OrgSelection selectedOrg={selectedOrg} setSelectedOrg={setSelectedOrg} />
      <UsersSelected selectedOrg={selectedOrg} />
    </>
  );
};

const roles = [
  { label: "None", value: "NONE" },
  { label: "Regular User", value: "REGULAR_USER" },
  { label: "Supervisor", value: "SUPERVISOR" },
  { label: "Administrator", value: "ADMINISTRATOR" },
];

const initialStateUserOrgs = [
  { label: "Entire Platform", value: "Entire Platform" },
];

const OrgSelection = ({ selectedOrg, setSelectedOrg }) => {
  const [userOrgOptions, setUserOrgOptions] = useState(initialStateUserOrgs);
  //TODO: Get max 250 orgs until we have a solution for selecting these options with large lists
  //Available Users Count
  const [{ data }] = useApi(availableOrganizations);

  useEffect(() => {
    if (data) {
      const enabledOrganizations = data?.availableOrganizations?.edges
        .filter((wg) => wg?.node?.enabled)
        .map((wg) => {
          return { label: wg?.node?.displayName, value: wg?.node?.id };
        });

      setUserOrgOptions((prevState) => [...prevState, ...enabledOrganizations]);
    }
  }, [data]);

  return (
    <>
      <FormControl>
        <Label>Filter Users By Organization</Label>
        <StyledSelect
          className={`react-select-container`}
          classNamePrefix={`react-select`}
          menuPortalTarget={document.body}
          name={`Org-Select`}
          id={`Org-Select`}
          inputId={`Org-Select-input`}
          instanceId={`Org-Select-instance`}
          options={userOrgOptions}
          onChange={(e) => setSelectedOrg(e)}
          value={selectedOrg}
        />
      </FormControl>
    </>
  );
};

const UsersSelected = ({ selectedOrg }) => {
  const [userData, setUserData] = useState([]);
  const [showUserSourcePermissions, setShowUserSourcePermissions] = useState();
  //TODO: Role UPDATE API is Broken currently, UI needs to indicate Loading and Completed Change
  const [{ errors: roleError, data: roleUpdateData }, doUpdateRole] = useApi();

  const updateRole = React.useCallback(
    (updateRoleEvent, userId) => {
      const updateUserRole = { id: userId, role: updateRoleEvent.value };
      const rolesToUpdate = updateUserRole;

      const variables = {
        userAccount: rolesToUpdate,
      };

      doUpdateRole({ query: setUsersRole, variables });
    },
    [doUpdateRole]
  );
  useEffect(() => {
    if (roleUpdateData && !roleError) {
      const affectedUser = roleUpdateData?.setUsersRole;

      setUserData((prevUsers) => {
        const userIndex = prevUsers.findIndex(
          (user) => user?.node?.id === affectedUser.id
        );

        if (userIndex !== -1) {
          const newUsers = update(prevUsers, {
            [userIndex]: { node: { $merge: { role: affectedUser.role } } },
          });
          return newUsers;
        } else {
          return prevUsers;
        }
      });
    }
  }, [roleUpdateData, setUserData, roleError]);

  const columnsData = [
    {
      Header: "Name",
      id: "name",
      accessor: (d) => d?.node?.displayName,
    },
    {
      Header: "Email",
      id: "emailAddress",
      accessor: (d) => d?.node?.emailAddress,
      Cell: ({ row: { original } }) => {
        return (
          <div
            style={{
              maxWidth: "300px",
              textOverflow: "ellipsis",
              overflow: "hidden",
            }}
            title={original?.node?.emailAddress}
          >
            {original?.node?.emailAddress}
          </div>
        );
      },
    },
    {
      Header: "Last Sign On Date",
      id: "lastSignOnDate",
      accessor: (d) => d?.node?.lastSignOnDate,
      Cell: ({ row: { original } }) => {
        return (
          original?.node?.lastSignOnDate &&
          formatTime(new Date(original?.node?.lastSignOnDate))
        );
      },
    },
    {
      Header: "Source Permissions",
      id: "sourcePermissions",
      Cell: ({ row: { original } }) => {
        return (
          <>
            <TableButton
              type="button"
              onClick={() =>
                setShowUserSourcePermissions({
                  id: original?.node?.id,
                  displayName: original?.node?.displayName,
                  emailAddress: original?.node?.emailAddress,
                })
              }
            >
              Source Permissions
            </TableButton>
          </>
        );
      },
    },
    {
      Header: () => (
        <div
          style={{
            minWidth: "165px",
            maxWidth: "165px",
          }}
        >
          Role
        </div>
      ),
      id: "role",
      style: {
        minWidth: "165px",
      },
      Cell: ({ row: { original } }) => {
        return (
          <StyledSelect
            className={`react-select-container`}
            classNamePrefix={`react-select`}
            menuPortalTarget={document.body}
            name={`${original?.node?.emailAddress}-role`}
            id={`${original?.node?.emailAddress}-role`}
            inputId={`${original?.node?.emailAddress}-role-input`}
            instanceId={`${original?.node?.emailAddress}-role-instance`}
            options={roles}
            onChange={(e) => updateRole(e, original?.node?.id)}
            placeholder={original?.node?.role}
            value={roles.find((role) => role.value === original?.node?.role)}
          />
        );
      },
    },
  ];

  //Init Data Fetch
  const [{ errors, loading, data: apiData }, doFetch] = useApi();
  const [pageSize, setPageSize] = useState(50);

  const totalCount = apiData?.usersInEnabledWorkGroups?.totalCount;
  const pageInfo = apiData?.usersInEnabledWorkGroups?.pageInfo;

  //Fetch for Table Paged
  const fetchData = useCallback(
    ({ pageSize: incomingPageSize, cursor }) => {
      const finalPageSize = incomingPageSize || pageSize;
      doFetch({
        query: usersInEnabledWorkGroups,
        variables: {
          first: finalPageSize,
          after: cursor,
          order: { displayName: "ASC" },
        },
      });
    },
    [doFetch, pageSize]
  );

  useEffect(() => {
    if (apiData) {
      const availUsers = apiData?.usersInEnabledWorkGroups?.edges ?? [];
      setUserData(availUsers);
    }
  }, [apiData]);

  const onSearch = useCallback(
    (text) => {
      const filterByOrgID =
        selectedOrg?.value === "Entire Platform" ? false : selectedOrg?.value;

      const where = {};
      if (text) {
        where.or = [{ displayName: { contains: text } }];
      }
      if (filterByOrgID) {
        where.organizationId = { eq: filterByOrgID };
      }

      const variables = {
        order: [{ displayName: "ASC" }],
        ...(Object.keys(where).length > 0 && { where }),
        first: pageSize, // Always use current page size
      };

      doFetch({
        query: usersInEnabledWorkGroups,
        variables,
      });
    },
    [doFetch, selectedOrg, pageSize]
  );

  const { inputText, handleInputChange, tooShortWarning } = useSearch({
    onSearch,
  });

  return (
    <>
      {showUserSourcePermissions ? (
        <Modal
          title={"View Source Permissions"}
          hide={() => setShowUserSourcePermissions()}
        >
          <ViewUsersDataSourcePermission
            viewUser={showUserSourcePermissions}
            setShowUserSourcePermissions={setShowUserSourcePermissions}
          />
        </Modal>
      ) : null}

      <FormControl style={{ maxWidth: "300px" }}>
        <SearchInput
          label="Search User"
          placeholder="Search User"
          inputText={inputText}
          handleChange={handleInputChange}
          warning={tooShortWarning}
        />

        {errors ? <ErrorMessages errors={errors} /> : null}
      </FormControl>

      <PagedTable
        fetchData={fetchData}
        loading={loading}
        pageInfo={pageInfo}
        totalCount={totalCount}
        data={userData}
        columns={columnsData}
        defaultPageSize={50}
        onPageSizeChange={(newSize) => setPageSize(newSize)}
      />
    </>
  );
};

const ExportDataSourcePermissions = () => {
  const [{ errors, loading }, doFetch] = useApiRedux();
  const exportDataSourcePermissions = () => {
    doFetch({
      query: "/datasources/permissions",
      method: "GET",
    });
    trackCustomEvent(
      "export",
      "Sources Permissions",
      "unknown-id",
      "Clicked Export Sources Permissions"
    );
  };
  return (
    <div style={{ marginBottom: "16px" }}>
      {loading ? (
        <Spinner />
      ) : (
        <Button
          width="250px"
          onClick={exportDataSourcePermissions}
          type="button"
          list="true"
          disabled={loading}
        >
          Export All Data Source Permissions
        </Button>
      )}
      {errors && <ErrorMessages errors={errors} />}
    </div>
  );
};

const ManageUsersPage = () => {
  return (
    <>
      <ExportDataSourcePermissions />
      <StaticUsersSection key={"StaticKeyRender"} />
    </>
  );
};

export default ManageUsersPage;
